import {
  type Block,
  type BlockType,
  type Deck,
  type Reference,
  type Section,
  flattenDeck,
  isEmptySection,
} from "@hali-shares/deck-schema";
import { useCallback, useEffect, useMemo, useState } from "react";
import { ApiError, type Asset, type Issue, type SessionSummary, type Version, api, issuesOf } from "../../lib/api";
import { slideIndexOf } from "../../deck/SlidePreview";
import { Drawer } from "./Drawer";
import { PreviewPanel } from "./PreviewPanel";
import {
  type AddReference,
  AddReferenceContext,
  ConfirmButton,
  Issues,
  LinesField,
  NumberField,
  TextField,
} from "./fields";
import { FlowEditor } from "./editors/FlowEditor";
import { ScreenshotEditor } from "./editors/ScreenshotEditor";
import { TimelineEditor } from "./editors/TimelineEditor";
import { VideoEditor } from "./editors/VideoEditor";
import { ConceptEditor, PollEditor, QuizEditor, ReferencesBlockEditor } from "./editors/SimpleEditors";
import type { EditorProps } from "./editors/types";
import { CONVERTIBLE, blockText, chipOf, convertBlock, issuesFor, slugify, uniqueId } from "./model";
import type { Finding, Target } from "./preflight";

/** Applies a new spec; resolves to the validation issues (empty = saved). */
export type Commit = (spec: Deck, note: string) => Promise<Issue[]>;

function withSection(deck: Deck, si: number, f: (s: Section) => Section): Deck {
  return { ...deck, sections: deck.sections.map((s, i) => (i === si ? f(s) : s)) };
}

function SaveBar({
  busy,
  disabled,
  onSave,
  onClose,
  left,
}: {
  busy: boolean;
  disabled?: boolean;
  onSave: () => void;
  onClose: () => void;
  left?: React.ReactNode;
}) {
  return (
    <>
      {left}
      <span className="spacer" />
      <button type="button" className="s-btn" onClick={onClose}>
        Cancel
      </button>
      <button type="button" className="s-btn s-btn-ink" disabled={busy || disabled} onClick={onSave}>
        {busy ? "Saving…" : "Save"}
      </button>
    </>
  );
}

/** What to type when asking the model for a new slide of each type. */
const DESCRIBE: Record<BlockType, string> = {
  concept: "e.g. three reasons evals beat vibe checks, with the cost numbers from my notes",
  flow: "e.g. a request hits the gateway, gets authenticated, then a worker handles it; failures retry",
  poll: "e.g. ask how often they ship prompt changes: weekly, monthly, rarely",
  quiz: "e.g. test whether people think a longer context always helps",
  screenshot: "e.g. two questions: where’s the bottleneck, what would you cache",
  video: "e.g. a short title and caption for the clip",
  timeline: "e.g. how we went from prompt engineering to context engineering, agents and harnesses",
  references: "e.g. only the papers from the agents section",
};

/* ---------- block ---------- */

export function BlockDrawer({
  deckId,
  aiEnabled,
  deck,
  sectionIndex,
  blockIndex,
  initial,
  assets,
  upload,
  commit,
  onClose,
}: {
  deckId: string;
  /** Whether the server can reach a model. */
  aiEnabled: boolean;
  deck: Deck;
  sectionIndex: number;
  /** null = a new block, appended on save. */
  blockIndex: number | null;
  initial: Block;
  assets: Asset[];
  upload: (f: File) => Promise<Asset | null>;
  commit: Commit;
  onClose: () => void;
}) {
  const [draft, setDraft] = useState<Block>(initial);
  // Bumped whenever the draft is replaced wholesale (AI rewrite, type change, undo), so the form starts fresh.
  const [rev, setRev] = useState(0);
  const [replaced, setReplaced] = useState<{ before: Block; label: string } | null>(null);
  const [ask, setAsk] = useState("");
  const [asking, setAsking] = useState(false);
  const [askError, setAskError] = useState<string | null>(null);
  const [issues, setIssues] = useState<Issue[]>([]);
  const [localErrors, setLocalErrors] = useState<string[]>([]);
  const [busy, setBusy] = useState(false);
  const section = deck.sections[sectionIndex]!;
  const index = blockIndex ?? section.blocks.length;
  const stableSetLocal = useCallback((e: string[]) => setLocalErrors(e), []);

  // References added from this drawer's footnote pickers; they join the deck when the block is saved.
  const [added, setAdded] = useState<Reference[]>([]);
  const view = useMemo(
    () => (added.length ? { ...deck, references: [...deck.references, ...added] } : deck),
    [deck, added],
  );
  const addReference = useCallback<AddReference>(
    async (raw) => {
      const known = view.references.find((r) => r.url === raw || r.url === `https://${raw}`);
      if (known) return { id: known.id, ok: true };
      try {
        const e = await api.enrich(raw);
        const again = view.references.find((r) => r.url === e.url);
        if (again) return { id: again.id, ok: true };
        const id = uniqueId(
          slugify(e.ok ? e.title : new URL(e.url).hostname, 30),
          view.references.map((r) => r.id),
        );
        const ref = Object.fromEntries(
          Object.entries({ id, url: e.url, title: e.title, authors: e.authors, venue: e.venue, year: e.year }).filter(
            ([, v]) => v !== undefined,
          ),
        ) as Reference;
        setAdded((list) => [...list, ref]);
        return { id, ok: e.ok };
      } catch {
        return { error: "That doesn’t look like a link." };
      }
    },
    [view],
  );

  const apply = async (next: Deck, note: string) => {
    setBusy(true);
    const found = await commit(next, note);
    setBusy(false);
    if (found.length === 0) onClose();
    else setIssues(issuesFor(found, sectionIndex, index).length ? issuesFor(found, sectionIndex, index) : found);
  };
  // The deck as it would be saved right now: what the preview shows, and what Save sends. A section's first real
  // slide takes the place of its "[To write]" placeholder.
  const draftDeck = useMemo(
    () =>
      withSection(view, sectionIndex, (s) => ({
        ...s,
        blocks:
          blockIndex === null
            ? isEmptySection(s)
              ? [draft]
              : [...s.blocks, draft]
            : s.blocks.map((b, i) => (i === blockIndex ? draft : b)),
      })),
    [view, sectionIndex, blockIndex, draft],
  );
  const save = () =>
    apply(
      draftDeck,
      `${blockIndex === null ? "Added" : "Edited"} ${chipOf(draft.type).toLowerCase()}: ${blockText(draft).slice(0, 60)}`,
    );
  const moveBy = (d: number) =>
    blockIndex !== null &&
    apply(
      withSection(deck, sectionIndex, (s) => {
        const blocks = [...s.blocks];
        const [b] = blocks.splice(blockIndex, 1);
        blocks.splice(Math.min(blocks.length, Math.max(0, blockIndex + d)), 0, b!);
        return { ...s, blocks };
      }),
      `Moved ${chipOf(draft.type).toLowerCase()}: ${blockText(draft).slice(0, 60)}`,
    );

  const replace = (next: Block, label: string) => {
    setReplaced((r) => ({ before: r?.before ?? draft, label }));
    setDraft(next);
    setLocalErrors([]);
    setRev((n) => n + 1);
  };
  const undoReplace = () => {
    if (!replaced) return;
    setDraft(replaced.before);
    setReplaced(null);
    setRev((n) => n + 1);
  };
  const rewrite = async (type?: BlockType) => {
    setAsking(true);
    setAskError(null);
    try {
      // A new slide's placeholder content means nothing: say so, so the model writes it from the description.
      const instruction =
        blockIndex === null && !type && ask.trim()
          ? `This is a new slide; ignore its placeholder content. Write it from this description: ${ask}`
          : ask;
      const r = await api.rewriteBlock(deckId, draftDeck, draft.id, instruction, type);
      replace(r.block, type ? `Rewritten by AI as a ${chipOf(type).toLowerCase()}.` : "Rewritten by AI.");
      setAsk("");
    } catch (e) {
      const found = issuesOf(e);
      const text = e instanceof ApiError || e instanceof Error ? e.message : String(e);
      setAskError(
        found.length
          ? `${text}: ${found
              .slice(0, 2)
              .map((i) => i.message)
              .join("; ")}`
          : text,
      );
    }
    setAsking(false);
  };
  const convertTo = (to: BlockType) => {
    const next = convertBlock(draft, to);
    if (next) replace(next, `Turned into a ${chipOf(to).toLowerCase()}.`);
    else void rewrite(to); // not enough to carry over by hand: let the model do it
  };

  const common = { deck: view, setLocalErrors: stableSetLocal, assets, upload };
  const editor = (() => {
    const p = <T extends Block>(b: T) =>
      ({ ...common, value: b, onChange: setDraft as (b: T) => void }) as EditorProps<T>;
    switch (draft.type) {
      case "concept":
        return <ConceptEditor {...p(draft)} />;
      case "flow":
        return <FlowEditor {...p(draft)} />;
      case "poll":
        return <PollEditor {...p(draft)} />;
      case "quiz":
        return <QuizEditor {...p(draft)} />;
      case "screenshot":
        return <ScreenshotEditor {...p(draft)} />;
      case "video":
        return <VideoEditor {...p(draft)} />;
      case "timeline":
        return <TimelineEditor {...p(draft)} />;
      case "references":
        return <ReferencesBlockEditor {...p(draft)} />;
    }
  })();

  return (
    <Drawer
      title={blockIndex === null ? `New ${chipOf(draft.type).toLowerCase()}` : blockText(draft) || "Untitled"}
      chip={<span className={`chip chip-${chipOf(draft.type)}`}>{chipOf(draft.type)}</span>}
      onClose={onClose}
      footer={
        <SaveBar
          busy={busy}
          disabled={localErrors.length > 0}
          onSave={save}
          onClose={onClose}
          left={
            blockIndex !== null && (
              <>
                <button
                  type="button"
                  className="s-btn s-btn-small"
                  disabled={busy || blockIndex === 0}
                  onClick={() => moveBy(-1)}
                >
                  Move up
                </button>
                <button
                  type="button"
                  className="s-btn s-btn-small"
                  disabled={busy || blockIndex === section.blocks.length - 1}
                  onClick={() => moveBy(1)}
                >
                  Move down
                </button>
                <ConfirmButton
                  className="s-btn s-btn-small s-btn-danger"
                  label="Delete"
                  confirmLabel="Confirm delete"
                  disabled={busy || section.blocks.length <= 1}
                  onConfirm={() =>
                    apply(
                      withSection(deck, sectionIndex, (s) => ({
                        ...s,
                        blocks: s.blocks.filter((_, i) => i !== blockIndex),
                      })),
                      `Deleted ${chipOf(draft.type).toLowerCase()}: ${blockText(draft).slice(0, 60)}`,
                    )
                  }
                />
              </>
            )
          }
        />
      }
    >
      <Issues issues={issues} />
      <PreviewPanel
        deck={draftDeck}
        slideIndex={slideIndexOf(draftDeck, draft.id)}
        sticky={draft.type !== "screenshot"} // the pin editor shows the image large already
      />
      {replaced && (
        <p className="banner banner-yellow undo-bar" role="status">
          {replaced.label} Check the preview, then Save to keep it.
          <button type="button" className="s-btn s-btn-small" onClick={undoReplace}>
            Undo
          </button>
        </p>
      )}
      <div className="ask-ai" data-new={blockIndex === null}>
        <label htmlFor={`ask-${draft.id}`}>
          {blockIndex === null
            ? `Describe this ${chipOf(draft.type).toLowerCase()} and AI drafts it`
            : "Ask AI to rewrite this slide"}
        </label>
        <div className="row-gap">
          <input
            id={`ask-${draft.id}`}
            type="text"
            value={ask}
            maxLength={1000}
            disabled={!aiEnabled}
            placeholder={
              blockIndex === null ? DESCRIBE[draft.type] : "e.g. shorter, add a zoom stop, turn it into three bullets"
            }
            onChange={(e) => setAsk(e.target.value)}
            onKeyDown={(e) => e.key === "Enter" && !asking && aiEnabled && void rewrite()}
          />
          <button
            type="button"
            className="s-btn s-btn-ink"
            disabled={asking || !aiEnabled}
            onClick={() => void rewrite()}
          >
            {asking
              ? blockIndex === null
                ? "Drafting…"
                : "Rewriting…"
              : blockIndex === null
                ? "Draft with AI"
                : "Rewrite"}
          </button>
        </div>
        {blockIndex === null && (
          <span className="field-hint">
            It uses the talk’s brief, this section and your notes. Or fill in the fields below yourself.
          </span>
        )}
        {(CONVERTIBLE[draft.type] ?? []).length > 0 && (
          <div className="row-gap convert-row">
            <span className="add-label">Change type</span>
            {(CONVERTIBLE[draft.type] ?? []).map((to) => (
              <button
                key={to}
                type="button"
                className="s-btn s-btn-small"
                disabled={asking || (!convertBlock(draft, to) && !aiEnabled)}
                onClick={() => convertTo(to)}
              >
                Make it a {chipOf(to).toLowerCase()}
                {!convertBlock(draft, to) && " (AI)"}
              </button>
            ))}
          </div>
        )}
        {askError && (
          <p className="banner banner-yellow" role="alert">
            {askError}
          </p>
        )}
      </div>
      <AddReferenceContext.Provider value={addReference}>
        <div key={rev} className="editor-fields">
          {editor}
        </div>
      </AddReferenceContext.Provider>
    </Drawer>
  );
}

/* ---------- section ---------- */

export function SectionDrawer({
  deck,
  sectionIndex,
  commit,
  onClose,
  onDeleted,
}: {
  deck: Deck;
  sectionIndex: number | null;
  commit: Commit;
  onClose: () => void;
  onDeleted: () => void;
}) {
  const existing = sectionIndex === null ? null : deck.sections[sectionIndex]!;
  const [draft, setDraft] = useState<Section>(
    existing ?? {
      id: uniqueId(
        "section",
        deck.sections.map((s) => s.id),
      ),
      title: "New section",
      minutes: 10,
      ahead: [],
      blocks: [
        {
          id: uniqueId(
            "concept",
            deck.sections.flatMap((s) => s.blocks.map((b) => b.id)),
          ),
          type: "concept",
          title: "[To write]",
          bullets: [],
          refs: [],
        },
      ],
    },
  );
  const [issues, setIssues] = useState<Issue[]>([]);
  const [busy, setBusy] = useState(false);
  const apply = async (next: Deck, note: string, after = onClose) => {
    setBusy(true);
    const found = await commit(next, note);
    setBusy(false);
    if (found.length === 0) after();
    else setIssues(found);
  };
  const set = (patch: Partial<Section>) => setDraft({ ...draft, ...patch });
  const moveTo = (to: number) => {
    if (sectionIndex === null) return;
    const sections = [...deck.sections];
    const [s] = sections.splice(sectionIndex, 1);
    sections.splice(to, 0, s!);
    void apply({ ...deck, sections }, `Moved section: ${draft.title}`);
  };

  return (
    <Drawer
      title={existing ? `Section ${sectionIndex! + 1}` : "New section"}
      onClose={onClose}
      footer={
        <SaveBar
          busy={busy}
          onClose={onClose}
          onSave={() =>
            apply(
              existing
                ? withSection(deck, sectionIndex!, () => draft)
                : { ...deck, sections: [...deck.sections, draft] },
              existing ? `Edited section: ${draft.title}` : `Added section: ${draft.title}`,
            )
          }
          left={
            existing && (
              <>
                <button
                  type="button"
                  className="s-btn s-btn-small"
                  disabled={busy || sectionIndex === 0}
                  onClick={() => moveTo(sectionIndex! - 1)}
                >
                  Move up
                </button>
                <button
                  type="button"
                  className="s-btn s-btn-small"
                  disabled={busy || sectionIndex === deck.sections.length - 1}
                  onClick={() => moveTo(sectionIndex! + 1)}
                >
                  Move down
                </button>
                <ConfirmButton
                  className="s-btn s-btn-small s-btn-danger"
                  label="Delete section"
                  confirmLabel="Confirm delete"
                  disabled={busy || deck.sections.length <= 1}
                  onConfirm={() =>
                    apply(
                      { ...deck, sections: deck.sections.filter((_, i) => i !== sectionIndex) },
                      `Deleted section: ${draft.title}`,
                      onDeleted,
                    )
                  }
                />
              </>
            )
          }
        />
      }
    >
      <Issues issues={issues} />
      <SectionPreview deck={deck} sectionIndex={sectionIndex} draft={draft} />
      <TextField label="Title" max={80} value={draft.title} onChange={(title) => set({ title })} />
      <NumberField
        label="Minutes"
        min={1}
        max={120}
        value={draft.minutes}
        onChange={(minutes) => set({ minutes: minutes ?? 1 })}
      />
      <LinesField
        label="What’s ahead (shown on the section slide)"
        hint="Up to 4 short lines."
        maxItems={4}
        value={draft.ahead}
        onChange={(ahead) => set({ ahead })}
      />
    </Drawer>
  );
}

/* ---------- deck details ---------- */

export function DeckDrawer({ deck, commit, onClose }: { deck: Deck; commit: Commit; onClose: () => void }) {
  const [draft, setDraft] = useState(deck);
  const [issues, setIssues] = useState<Issue[]>([]);
  const [busy, setBusy] = useState(false);
  const set = (patch: Partial<Deck>) => setDraft({ ...draft, ...patch });
  return (
    <Drawer
      title="Talk details"
      onClose={onClose}
      footer={
        <SaveBar
          busy={busy}
          onClose={onClose}
          onSave={async () => {
            setBusy(true);
            const found = await commit(
              {
                ...deck,
                title: draft.title,
                subtitle: draft.subtitle,
                presenter: draft.presenter,
                event: draft.event,
                date: draft.date,
              },
              "Edited talk details",
            );
            setBusy(false);
            if (found.length) setIssues(found);
            else onClose();
          }}
        />
      }
    >
      <Issues issues={issues} />
      <PreviewPanel deck={draft} slideIndex={0} />
      <TextField label="Title" max={80} value={draft.title} onChange={(title) => set({ title })} />
      <TextField
        label="Subtitle"
        max={160}
        value={draft.subtitle}
        onChange={(subtitle) => set({ subtitle: subtitle || undefined })}
      />
      <TextField label="Presenter" max={60} value={draft.presenter} onChange={(presenter) => set({ presenter })} />
      <div className="grid-2">
        <TextField
          label="Event"
          max={60}
          value={draft.event}
          placeholder="e.g. Team meetup"
          onChange={(event) => set({ event: event || undefined })}
        />
        <div className="field">
          <label className="field-label" htmlFor="deck-date">
            Date
          </label>
          <input
            id="deck-date"
            className="input input-mono"
            type="date"
            value={draft.date ?? ""}
            onChange={(e) => set({ date: e.target.value || undefined })}
          />
        </div>
      </div>
    </Drawer>
  );
}

/* ---------- reference ---------- */

type Citer = { key: string; label: string; has: (d: Deck) => boolean; toggle: (d: Deck, on: boolean) => Deck };

/** Blocks (and zoomed flow steps) in a section that can cite a reference. */
function citers(deck: Deck, si: number, refId: string): Citer[] {
  const out: Citer[] = [];
  const edit = (d: Deck, bi: number, f: (b: Block) => Block) =>
    withSection(d, si, (s) => ({ ...s, blocks: s.blocks.map((b, i) => (i === bi ? f(b) : b)) }));
  const flip = (list: string[], on: boolean) =>
    on ? (list.includes(refId) ? list : [...list, refId]) : list.filter((x) => x !== refId);
  deck.sections[si]!.blocks.forEach((b, bi) => {
    if (b.type === "concept" || b.type === "flow" || b.type === "screenshot")
      out.push({
        key: b.id,
        label: `${chipOf(b.type)} · ${blockText(b)}`,
        has: (d) => {
          const x = d.sections[si]!.blocks[bi]!;
          return "refs" in x && x.refs.includes(refId);
        },
        toggle: (d, on) => edit(d, bi, (x) => ("refs" in x ? { ...x, refs: flip(x.refs, on) } : x) as Block),
      });
    if (b.type === "flow")
      b.nodes.forEach((n, ni) => {
        if (!n.detail) return;
        out.push({
          key: `${b.id}/${n.id}`,
          label: `${blockText(b)} › ${n.label} (zoomed)`,
          has: (d) => {
            const x = d.sections[si]!.blocks[bi]!;
            return x.type === "flow" && Boolean(x.nodes[ni]?.detail?.refs.includes(refId));
          },
          toggle: (d, on) =>
            edit(d, bi, (x) =>
              x.type === "flow"
                ? {
                    ...x,
                    nodes: x.nodes.map((m, j) =>
                      j === ni && m.detail ? { ...m, detail: { ...m.detail, refs: flip(m.detail.refs, on) } } : m,
                    ),
                  }
                : x,
            ),
        });
      });
  });
  return out;
}

function stripRef(deck: Deck, id: string): Deck {
  const drop = (l: string[]) => l.filter((x) => x !== id);
  return {
    ...deck,
    references: deck.references.filter((r) => r.id !== id),
    sections: deck.sections.map((s) => ({
      ...s,
      blocks: s.blocks.map((b): Block => {
        if (b.type === "references") return b.referenceIds ? { ...b, referenceIds: drop(b.referenceIds) } : b;
        if (b.type === "flow")
          return {
            ...b,
            refs: drop(b.refs),
            nodes: b.nodes.map((n) => (n.detail ? { ...n, detail: { ...n.detail, refs: drop(n.detail.refs) } } : n)),
          };
        if ("refs" in b) return { ...b, refs: drop(b.refs) } as Block;
        return b;
      }),
    })),
  };
}

export function ReferenceDrawer({
  deck,
  sectionIndex,
  refId,
  commit,
  onClose,
}: {
  deck: Deck;
  sectionIndex: number;
  /** null = new reference */
  refId: string | null;
  commit: Commit;
  onClose: () => void;
}) {
  const existing = refId ? deck.references.find((r) => r.id === refId) : undefined;
  const [draft, setDraft] = useState<Reference>(existing ?? { id: "", title: "", url: "" });
  const id = existing?.id ?? "__new__";
  const options = citers(deck, sectionIndex, id);
  // A new reference starts as a footnote on the section's first block that can carry one, so it shows up straight away.
  const [cited, setCited] = useState<string[]>(() =>
    existing ? options.filter((c) => c.has(deck)).map((c) => c.key) : options[0] ? [options[0].key] : [],
  );
  const [issues, setIssues] = useState<Issue[]>([]);
  const [busy, setBusy] = useState(false);
  const [looking, setLooking] = useState(false);
  const [note, setNote] = useState<string | null>(null);
  const set = (patch: Partial<Reference>) => setDraft({ ...draft, ...patch });

  const lookUp = async () => {
    setLooking(true);
    setNote(null);
    try {
      const e = await api.enrich(draft.url);
      setDraft({ ...draft, url: e.url, title: e.title, authors: e.authors, venue: e.venue, year: e.year });
      setNote(
        e.ok
          ? "Filled in from the link. Check it reads right."
          : "Couldn’t read that link. Fill in the title yourself.",
      );
    } catch {
      setNote("Couldn’t look that up.");
    }
    setLooking(false);
  };

  const save = async () => {
    const finalId =
      existing?.id ??
      uniqueId(
        slugify(draft.title || "ref", 30),
        deck.references.map((r) => r.id),
      );
    const ref: Reference = Object.fromEntries(
      Object.entries({ ...draft, id: finalId }).filter(([, v]) => v !== undefined && v !== ""),
    ) as Reference;
    let next: Deck = existing
      ? { ...deck, references: deck.references.map((r) => (r.id === finalId ? ref : r)) }
      : { ...deck, references: [...deck.references, ref] };
    for (const c of citers(next, sectionIndex, finalId)) next = c.toggle(next, cited.includes(c.key));
    setBusy(true);
    const found = await commit(next, `${existing ? "Edited" : "Added"} reference: ${ref.title.slice(0, 60)}`);
    setBusy(false);
    if (found.length) setIssues(found);
    else onClose();
  };

  return (
    <Drawer
      title={existing ? "Reference" : "New reference"}
      chip={<span className="chip chip-REFERENCE">REFERENCE</span>}
      onClose={onClose}
      footer={
        <SaveBar
          busy={busy}
          disabled={!draft.url || !draft.title}
          onClose={onClose}
          onSave={save}
          left={
            existing && (
              <ConfirmButton
                className="s-btn s-btn-small s-btn-danger"
                label="Remove from deck"
                confirmLabel="Confirm remove"
                onConfirm={async () => {
                  setBusy(true);
                  const found = await commit(
                    stripRef(deck, existing.id),
                    `Removed reference: ${existing.title.slice(0, 60)}`,
                  );
                  setBusy(false);
                  if (found.length) setIssues(found);
                  else onClose();
                }}
              />
            )
          }
        />
      }
    >
      <Issues issues={issues} />
      <div className="field">
        <label className="field-label" htmlFor="ref-url">
          Link
        </label>
        <div className="row-gap" style={{ flexWrap: "nowrap" }}>
          <input
            id="ref-url"
            className="input input-mono"
            value={draft.url}
            placeholder="https://arxiv.org/abs/…"
            onChange={(e) => set({ url: e.target.value })}
          />
          <button type="button" className="s-btn" disabled={!draft.url || looking} onClick={lookUp}>
            {looking ? "Looking up…" : "Fill from link"}
          </button>
        </div>
        <span className="field-hint">
          Title, authors and year come from the page itself (arXiv links use arXiv’s own record).
        </span>
      </div>
      {note && <p className="banner banner-grey">{note}</p>}
      <TextField label="Title" value={draft.title} onChange={(title) => set({ title })} />
      <div className="grid-2">
        <TextField
          label="Authors"
          placeholder="e.g. Smith et al."
          value={draft.authors}
          onChange={(authors) => set({ authors: authors || undefined })}
        />
        <TextField
          label="Venue"
          placeholder="e.g. a conference or journal"
          value={draft.venue}
          onChange={(venue) => set({ venue: venue || undefined })}
        />
      </div>
      <NumberField label="Year" min={1900} max={2100} optional value={draft.year} onChange={(year) => set({ year })} />
      <div className="field">
        <span className="field-label">Footnote on</span>
        {options.length === 0 ? (
          <p className="empty">No block in this section can carry a footnote. It still appears in “Read more”.</p>
        ) : (
          <div className="refs-pick">
            {options.map((c) => (
              <label key={c.key} className="check">
                <input
                  type="checkbox"
                  checked={cited.includes(c.key)}
                  onChange={(e) => setCited(e.target.checked ? [...cited, c.key] : cited.filter((k) => k !== c.key))}
                />
                {c.label}
              </label>
            ))}
          </div>
        )}
      </div>
    </Drawer>
  );
}

/* ---------- versions ---------- */

export function VersionsDrawer({
  deckId,
  current,
  onRestored,
  onClose,
}: {
  deckId: string;
  current: number;
  onRestored: (record: Awaited<ReturnType<typeof api.restore>>) => void;
  onClose: () => void;
}) {
  const [versions, setVersions] = useState<Version[] | null>(null);
  const [busy, setBusy] = useState<number | null>(null);
  useEffect(() => {
    api.versions(deckId).then(setVersions, () => setVersions([]));
  }, [deckId, current]);
  return (
    <Drawer title="Versions" onClose={onClose}>
      <p className="empty">
        Every save is a version. Restoring brings an old one back as the newest, so nothing is lost.
      </p>
      {versions === null ? (
        <span className="busy">LOADING</span>
      ) : (
        <ul className="versions">
          {versions.map((v) => (
            <li key={v.number} className="version">
              <span className="version-n">v{v.number}</span>
              <span className="version-main">
                <span className="version-note">{v.note ?? "Saved"}</span>
                <span className="version-time">{new Date(v.createdAt).toLocaleString()}</span>
              </span>
              {v.number === current ? (
                <span className="busy">CURRENT</span>
              ) : (
                <ConfirmButton
                  className="s-btn s-btn-small"
                  label={busy === v.number ? "Restoring…" : "Restore"}
                  confirmLabel={`Restore v${v.number}?`}
                  disabled={busy !== null}
                  onConfirm={async () => {
                    setBusy(v.number);
                    try {
                      onRestored(await api.restore(deckId, v.number));
                    } finally {
                      setBusy(null);
                    }
                  }}
                />
              )}
            </li>
          ))}
        </ul>
      )}
    </Drawer>
  );
}

/* ---------- sessions ---------- */

export function SessionsDrawer({ deckId, onClose }: { deckId: string; onClose: () => void }) {
  const [sessions, setSessions] = useState<SessionSummary[] | null>(null);
  useEffect(() => {
    api.sessionsFor(deckId).then(setSessions, () => setSessions([]));
  }, [deckId]);
  return (
    <Drawer title="Sessions" onClose={onClose}>
      <p className="empty">
        Every live run of this talk. Download the responses as CSV: one row per answer, with the question, the option
        chosen and, for quizzes, whether it was right.
      </p>
      {sessions === null ? (
        <span className="busy">LOADING</span>
      ) : sessions.length === 0 ? (
        <p className="empty">No live sessions yet. “Start live session” runs one.</p>
      ) : (
        <ul className="versions">
          {sessions.map((s) => (
            <li key={s.code} className="version">
              <span className="version-n">{s.code}</span>
              <span className="version-main">
                <span className="version-note">
                  {new Date(s.createdAt).toLocaleString()} · {s.status === "LIVE" ? "live now" : "ended"}
                </span>
                <span className="version-time">
                  {s.participants} {s.participants === 1 ? "person" : "people"} · {s.votes}{" "}
                  {s.votes === 1 ? "response" : "responses"} · deck v{s.deckVersion}
                </span>
              </span>
              <a className="s-btn s-btn-small" href={`/api/sessions/${s.code}/responses.csv`} download>
                Download CSV
              </a>
            </li>
          ))}
        </ul>
      )}
    </Drawer>
  );
}

/** The section slide (number, title, what's ahead) as the draft would show it. */
function SectionPreview({ deck, sectionIndex, draft }: { deck: Deck; sectionIndex: number | null; draft: Section }) {
  const next = useMemo(
    () =>
      sectionIndex === null
        ? { ...deck, sections: [...deck.sections, draft] }
        : withSection(deck, sectionIndex, () => draft),
    [deck, sectionIndex, draft],
  );
  const index = flattenDeck(next).findIndex((s) => s.kind === "section" && s.sectionId === draft.id);
  return <PreviewPanel deck={next} slideIndex={index} />;
}

/* ---------- before going live ---------- */

export function PreflightDrawer({
  deckId,
  findings,
  onOpen,
  onClose,
}: {
  deckId: string;
  findings: Finding[];
  onOpen: (t: Target) => void;
  onClose: () => void;
}) {
  const fix = findings.filter((f) => f.level === "fix");
  return (
    <Drawer
      title="Before you go live"
      onClose={onClose}
      footer={
        <>
          <span className="spacer" />
          <a className="s-btn" href={`/present/${deckId}?rehearse=1`} target="_blank" rel="noopener">
            Rehearse
          </a>
          <a
            className="s-btn s-btn-yellow"
            href={`/present/${deckId}`}
            target="_blank"
            rel="noopener"
            onClick={onClose}
          >
            {fix.length ? "Start anyway" : "Start live session"}
          </a>
        </>
      }
    >
      <FindingsList findings={findings} onOpen={onOpen} />
    </Drawer>
  );
}

/** The pre-live findings, grouped: what the room would see, then what's worth a look. Each opens its fix. */
export function FindingsList({ findings, onOpen }: { findings: Finding[]; onOpen: (t: Target) => void }) {
  const fix = findings.filter((f) => f.level === "fix");
  const check = findings.filter((f) => f.level === "check");
  const list = (items: Finding[]) => (
    <ul className="findings">
      {items.map((f, i) => (
        <li key={i}>
          {f.target.kind === "none" ? (
            <span className="finding">
              <span className="finding-where">{f.where}</span>
              <span>{f.message}</span>
            </span>
          ) : (
            <button type="button" className="finding" onClick={() => onOpen(f.target)}>
              <span className="finding-where">{f.where}</span>
              <span>{f.message}</span>
            </button>
          )}
        </li>
      ))}
    </ul>
  );
  return (
    <>
      {fix.length > 0 && (
        <section className="finding-group">
          <h3 className="col-label">The room would see these · {fix.length}</h3>
          {list(fix)}
        </section>
      )}
      {check.length > 0 && (
        <section className="finding-group">
          <h3 className="col-label">Worth a look · {check.length}</h3>
          {list(check)}
        </section>
      )}
      {findings.length === 0 && <p className="empty">Nothing to fix. Good to go.</p>}
    </>
  );
}
